import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  ApolloError,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GraphQLQuery } from "@/lib/graphql-query";

const httpLink = createHttpLink({
  uri: `${import.meta.env.VITE_API_URL}/graphql`,
});

const authLink = setContext((_, { headers }) => {
  let token = null;
  const userSession = sessionStorage.getItem("user");
  if (userSession) {
    const user = JSON.parse(userSession);
    token = user.token;
  }
  if (token) {
    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : "",
      },
    };
  }
  return headers;
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => console.error(`[GraphQL error]: `, error));
  }
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        receivedFriendRequests: {
          // @ts-ignore
          merge(existing, incoming) {
            // 새로운 데이터로 완전히 교체
            return incoming;
          },
        },
        sentFriendRequests: {
          // @ts-ignore
          merge(existing, incoming) {
            // 새로운 데이터로 완전히 교체
            return incoming;
          },
        },
        fetchChats: {
          // @ts-ignore
          merge(existing, incoming) {
            // 새로운 데이터로 완전히 교체
            return incoming;
          },
        },
      },
    },
  },
});

export const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLink]),
  // cache: new InMemoryCache(),
  cache,
});

export function getErrorMessage(error: any) {
  if (error instanceof ApolloError) {
    const cause: any = error.cause;
    if (cause?.extensions.code) {
      return `[${cause.extensions.code}]: ${cause.message}`;
    } else {
      return cause?.extensions?.originalError.message;
    }
  }
  return error.message;
}

export class GraphQLClient {
  private static instance: GraphQLClient;

  public static getInstance(): GraphQLClient {
    if (!GraphQLClient.instance) {
      GraphQLClient.instance = new GraphQLClient();
    }
    return GraphQLClient.instance;
  }

  private constructor() {}

  async signIn({
    email,
    password,
  }: {
    email: string;
    password: string;
  }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.signIn(email, password),
    });
    console.log("[GraphQL] signIn", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.signIn;
  }

  async signUp({
    name,
    email,
    password,
  }: {
    name: string;
    email: string;
    password: string;
  }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.signUp(name, email, password),
    });
    console.log("[GraphQL] signUp", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.signUp;
  }

  async changeName({ newName }: { newName: string }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.changeName(newName),
    });
    console.log("[GraphQL] changeName", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.changeName;
  }

  async changePassword({
    oldPassword,
    newPassword,
  }: {
    oldPassword: string;
    newPassword: string;
  }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.changePassword(oldPassword, newPassword),
    });
    console.log("[GraphQL] changePassword", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.changePassword;
  }

  async updateUserData({ newData }: { newData: any }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.updateUserData(), // 쿼리 틀만 가져옴
      variables: { newData }, // 데이터를 별도로 전달 (안전)
    });
    console.log("[GraphQL] updateUserData", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.updateUserData;
  }

  async updateUserDataField({ newData }: { newData: any }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.updateUserDataField(),
      variables: { newData },
    });
    console.log("[GraphQL] updateUserDataField", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.updateUserDataField;
  }

  async searchUser(
    { search }: { search: string },
    forceUpdate: boolean = false,
  ): Promise<any> {
    const response = await client.query({
      query: GraphQLQuery.searchUser(search),
      fetchPolicy: forceUpdate ? "network-only" : "cache-first",
    });
    console.log("[GraphQL] searchUser", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.allUsers;
  }

  async getMyInfo(forceUpdate: boolean = false): Promise<any> {
    const response = await client.query({
      query: GraphQLQuery.getMyInfo,
      fetchPolicy: forceUpdate ? "network-only" : "cache-first",
    });
    console.log("[GraphQL] getMyInfo", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.getMyInfo;
  }

  async sendFriendRequest({ userId }: { userId: string }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.sendFriendRequest(userId),
    });
    console.log("[GraphQL] sendFriendRequest", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.sendFriendRequest;
  }

  async cancelFriendRequest({
    friendshipId,
  }: {
    friendshipId: string;
  }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.cancelFriendRequest(friendshipId),
    });
    console.log("[GraphQL] cancelFriendRequest", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.cancelFriendRequest;
  }

  async acceptFriendRequest({
    friendshipId,
  }: {
    friendshipId: string;
  }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.acceptFriendRequest(friendshipId),
    });
    console.log("[GraphQL] acceptFriendRequest", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.acceptFriendRequest;
  }

  async rejectFriendRequest({
    friendshipId,
  }: {
    friendshipId: string;
  }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.rejectFriendRequest(friendshipId),
    });
    console.log("[GraphQL] rejectFriendRequest", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.rejectFriendRequest;
  }

  async sentFriendRequests(forceUpdate: boolean = false): Promise<any> {
    const response = await client.query({
      query: GraphQLQuery.sentFriendRequests,
      fetchPolicy: forceUpdate ? "network-only" : "cache-first",
    });
    console.log("[GraphQL] sentFriendRequests", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.sentFriendRequests;
  }

  async receivedFriendRequests(forceUpdate: boolean = false): Promise<any> {
    const response = await client.query({
      query: GraphQLQuery.receivedFriendRequests,
      fetchPolicy: forceUpdate ? "network-only" : "cache-first",
    });
    console.log("[GraphQL] receivedFriendRequests", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.receivedFriendRequests;
  }

  async fetchChats(forceUpdate: boolean = false): Promise<any> {
    const response = await client.query({
      query: GraphQLQuery.fetchChats,
      fetchPolicy: forceUpdate ? "network-only" : "cache-first",
    });
    console.log("[GraphQL] fetchChats", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.fetchChats;
  }

  async getFriends(forceUpdate: boolean = false): Promise<any> {
    const response = await client.query({
      query: GraphQLQuery.getFriends,
      fetchPolicy: forceUpdate ? "network-only" : "cache-first",
    });
    console.log("[GraphQL] getFriends", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.getFriends;
  }

  async deleteChat({ chatId }: { chatId: string }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.deleteChat(chatId),
    });
    console.log("[GraphQL] deleteChat", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.deleteChat;
  }

  async createGroupChat({
    chatName,
    users,
  }: {
    chatName: string;
    users: string[];
  }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.createGroupChat(chatName, users),
    });
    console.log("[GraphQL] createGroupChat", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.createGroupChat;
  }

  async updateGroupChat({
    chatId,
    chatName,
    admin,
    users,
  }: {
    chatId: string;
    chatName: string;
    admin: string;
    users: string[];
  }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.updateGroupChat(chatId, chatName, admin, users),
    });
    console.log("[GraphQL] updateGroupChat", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.updateGroupChat;
  }

  async deleteGroupChat({ chatId }: { chatId: string }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.deleteGroupChat(chatId),
    });
    console.log("[GraphQL] deleteGroupChat", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.deleteGroupChat;
  }

  async removeUserFromGroupChat({
    chatId,
    userId,
  }: {
    chatId: string;
    userId: string;
  }): Promise<any> {
    const response = await client.mutate({
      mutation: GraphQLQuery.removeFromGroup(chatId, userId),
    });
    console.log("[GraphQL] removeUserFromGroupChat", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.removeFromGroup;
  }

  async allMessages(
    {
      chatId,
      messageId,
      endMessageId,
      count,
    }: {
      chatId: string;
      messageId?: string;
      endMessageId?: string;
      count?: number;
    },
    forceUpdate: boolean = false,
  ): Promise<any> {
    const response = await client.query({
      query: GraphQLQuery.allMessages(chatId, messageId, endMessageId, count),
      fetchPolicy: forceUpdate ? "network-only" : "cache-first",
    });
    console.log("[GraphQL] allMessages", response.data);
    if (response.errors) {
      throw new Error(JSON.stringify(response.errors));
    }
    return response.data.allMessages;
  }
}

export const graphQLClient = GraphQLClient.getInstance();
